// =========================================================
// Vojjin Observer Utilities
// =========================================================

import {registerGlobal} from "./core.js";

/* global Logger */

class Observe {

    // Watch when elements enter or leave viewport
    /**
     * Observes elements matching the specified selector for changes in their visibility within the viewport.
     *
     * @function visibility
     * @param {string} selector - A CSS selector string to target the elements to be observed.
     * @param {function} callback - A callback function invoked whenever an observed element changes its intersection state.
     *        The callback receives the following arguments:
     *        - {Element} target - The observed element.
     *        - {boolean} isIntersecting - A boolean indicating whether the element is currently intersecting the viewport.
     *        - {IntersectionObserverEntry} entry - The full IntersectionObserverEntry object.
     * @param {Object} [opts={}] - Optional configuration object for the IntersectionObserver.
     *        - {number} [opts.threshold=0.1] - A threshold value controlling when the callback is triggered, as a ratio of element visibility.
     *        - {Element} [opts.root=null] - The root element to use for intersection tests, or `null` for the viewport.
     *        - {string} [opts.rootMargin='0px'] - Margin around the root element to expand or shrink the viewport boundaries.
     * @returns {IntersectionObserver|null} Returns the IntersectionObserver instance for manual disconnection, or `null` if no elements are defined.
     */
    static visibility = (selector, callback, opts = {}) => {
        const els = $$(selector);
        if (!els.defined()) return null;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => callback(entry.target, entry.isIntersecting, entry));
            },
            {
                threshold: opts.threshold ?? 0.1,
                root: opts.root ?? null,
                rootMargin: opts.rootMargin ?? '0px'
            }
        );
        els.each(el => observer.observe(el));
        return observer; // return for manual disconnect if needed
    }

    // Watch when DOM elements are added, removed, or modified
    /**
     * Observes DOM element(s) for mutations and executes a callback function when changes are detected.
     *
     * @param {string} selector - A CSS selector to identify the target element(s) to observe.
     * @param {Function} callback - A function to execute when mutations are observed. The function receives
     *                              an array of MutationRecord objects as its argument.
     * @param {Object} [opts={}] - An optional configuration object to customize the observation. It extends
     *                             the default options:
     *                              - `childList`: (boolean) Whether to observe changes to the child nodes of the target(s).
     *                              - `subtree`: (boolean) Whether to observe changes for the entire subtree of the target(s).
     *                              - `attributes`: (boolean) Whether to observe changes to attributes of the target(s).
     *                              - `characterData`: (boolean) Whether to observe changes to character data of the target(s).
     * @returns {MutationObserver|null} - The created MutationObserver instance if the selector matches, or null if no matches are found.
     */
    static dom = (selector, callback, opts = {}) => {
        const els = $$(selector);
        if (!els.defined()) return null;
        const config = Object.assign(
            {
                childList: true,
                subtree: true,
                attributes: false,
                characterData: false
            },
            opts
        );

        const observer = new MutationObserver((mutations) => callback(mutations));
        els.each(el => observer.observe(el, config));
        return observer;
    }

    // Watch for new elements that match a selector when they appear in DOM
    /**
     * Tracks the addition and removal of DOM elements matching a specific selector within a given root.
     * Invokes a callback for each matching element when they are added or removed.
     *
     * @param {string} selector - The CSS selector to match elements.
     * @param {Function} callback - A function to execute for each matching element. The function
     *                              receives two arguments:
     *                                - {Element} el: The element added or removed.
     *                                - {boolean} isAdded: `true` if the element was added, `false` if removed.
     * @param {Element} [root=document.body] - The root element under which to observe for changes. Defaults to the entire document body.
     * @param {Object} [opts={}] - Optional configuration for the MutationObserver. By default,
     *                             it observes changes in child nodes and their subtrees.
     * @returns {MutationObserver|null} - A configured `MutationObserver` instance observing the changes,
     *                                     or `null` if no matching elements exist initially.
     */
    static live = (selector, callback, root = document.body, opts = {}) => {
        // Immediately handle any existing matching elements
        const existing = $$(selector, root);
        if (!existing.defined()) return null;
        existing.each(el => callback(el, true)); // "true" = initial

        // Watch for future elements being added
        const config = Object.assign(
            {
                childList: true,
                subtree: true
            },
            opts
        );

        const observer = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    // Check if the added node itself matches
                    if (node.nodeType === 1 && node.matches(selector)) {
                        callback(node, true);
                    }
                    // Check any matching descendants
                    if (node.nodeType === 1) {
                        node.querySelectorAll(selector).forEach(child => callback(child, true));
                    }
                });

                mutation.removedNodes.forEach(node => {
                    if (node.nodeType === 1 && node.matches(selector)) {
                        callback(node, false);
                    }
                    if (node.nodeType === 1) {
                        node.querySelectorAll(selector).forEach(child => callback(child, false));
                    }
                });
            });
        });

        observer.observe(root, config);
        return observer;
    };

    // Watch when element size changes (width/height)
    /**
     * Attaches a resize observer to elements matching the specified selector and triggers a callback
     * whenever a resize event occurs on any of the observed elements.
     *
     * @param {string} selector - A CSS selector to identify the elements to observe.
     * @param {Function} callback - A function to execute when a resize event is detected.
     * The function receives two arguments: the resized element and its new content rectangle.
     * @returns {ResizeObserver|null} - Returns the ResizeObserver instance if the elements are defined;
     * otherwise, returns null.
     */
    static resize = (selector, callback) => {
        const els = $$(selector);
        if (!els.defined()) return null;
        const observer = new ResizeObserver((entries) => {
            entries.forEach(entry => callback(entry.target, entry.contentRect));
        });
        els.each(el => observer.observe(el));
        return observer;
    }

    // Stop observing
    /**
     * Stops and disconnects the given observer if it is valid and has a `disconnect` method.
     *
     * @param {MutationObserver|IntersectionObserver|ResizeObserver} observer - The observer instance to be stopped.
     *                                                                         It should have a `disconnect` method.
     */
    static stop = (observer) => {
        if (observer && observer.disconnect) observer.disconnect();
    }
}

registerGlobal("Observe", Observe);
